import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

const MAX_SCALE = 4;
const ZOOM_STEP_THRESHOLD = 0.12;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getCurrentScale(api) {
  return (
    api?.instance?.transformState?.scale ??
    api?.instance?.state?.scale ??
    api?.state?.scale ??
    1
  );
}

function getNativeScale(image) {
  if (!image.naturalWidth || !image.clientWidth) return 2;
  return clamp(image.naturalWidth / image.clientWidth, 1.6, MAX_SCALE);
}

function getCenteredTransform(api, scale) {
  const wrapper = api?.instance?.wrapperComponent;
  const content = api?.instance?.contentComponent;

  if (!wrapper || !content) {
    return { positionX: 0, positionY: 0, scale };
  }

  return {
    positionX: (wrapper.offsetWidth - content.offsetWidth * scale) / 2,
    positionY: (wrapper.offsetHeight - content.offsetHeight * scale) / 2,
    scale,
  };
}

export default function PlancheLightbox({ src, alt = "Planche BD", onClose }) {
  const transformRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (!src) return null;

  const handleZoomStep = (event) => {
    event.preventDefault();

    const api = transformRef.current;
    if (!api) return;

    const native = getNativeScale(event.currentTarget);
    const high = clamp(Math.max(2.5, native), 1, MAX_SCALE);
    const scale = getCurrentScale(api);
    let nextScale = 1;

    if (scale < native - ZOOM_STEP_THRESHOLD) {
      nextScale = native;
    } else if (scale < high - ZOOM_STEP_THRESHOLD) {
      nextScale = high;
    }

    if (nextScale === 1 && typeof api.resetTransform === "function") {
      api.resetTransform();
      return;
    }

    if (typeof api.centerView === "function") {
      api.centerView(nextScale, 200);
      return;
    }

    if (typeof api.setTransform === "function") {
      const centered = getCenteredTransform(api, nextScale);
      api.setTransform(centered.positionX, centered.positionY, centered.scale, 200);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Planche BD agrandie"
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-3 sm:p-6"
      data-testid="planche-lightbox-backdrop"
      onClick={(event) => {
        if (!event.target.closest("[data-planche-lightbox-image]")) onClose();
      }}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        aria-label="Fermer"
        title="Fermer"
        className="absolute right-3 top-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-gray-900 shadow-lg hover:bg-white focus:outline-none focus:ring-2 focus:ring-white"
      >
        <X className="h-6 w-6" aria-hidden="true" />
      </button>

      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={1}
        maxScale={MAX_SCALE}
        centerOnInit
        limitToBounds={false}
        wheel={{ disabled: false, step: 0.12 }}
        pinch={{ disabled: false, step: 5 }}
        panning={{ disabled: false, velocityDisabled: true }}
        doubleClick={{ disabled: true }}
        smooth
      >
        <TransformComponent
          wrapperStyle={{
            width: "100vw",
            height: "100vh",
          }}
          contentStyle={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "3.5rem 1rem 1.25rem",
          }}
        >
          <img
            src={src}
            alt={alt}
            data-planche-lightbox-image
            className="max-h-full max-w-full select-none rounded bg-white shadow-2xl"
            draggable={false}
            onDoubleClick={handleZoomStep}
          />
        </TransformComponent>
      </TransformWrapper>
    </div>,
    document.body,
  );
}
